import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Beaker, Flame, Quote, GraduationCap, Activity, Thermometer, BookOpen, ExternalLink,
} from "lucide-react";
import RDSimulationPanel from "@/components/portal/RDSimulationPanel";
import EPSSimulationPanel from "@/components/portal/EPSSimulationPanel";
import RockwoolModule from "@/components/portal/RockwoolModule";
import PURModule from "@/components/portal/PURModule";
import GlasswoolModule from "@/components/portal/GlasswoolModule";
import { useI18n } from "@/lib/i18n";

function StatBox({ label, value, unit, accent, light }) {
  const color = light ? "#000000" : (accent || "#FFFFFF");
  const bg = light ? "#FFFFFF" : "#0A0A0A";
  const borderC = light ? "#E5E7EB" : "#1F1F1F";
  return (
    <div className="border p-3" style={{ backgroundColor: bg, borderColor: borderC }} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="tech-label" style={{ color, WebkitTextFillColor: color, fontWeight: 700, opacity: 1 }}>{label}</div>
      <div className="font-mono text-xl" style={{ color, WebkitTextFillColor: color, fontWeight: 800, opacity: 1 }}>
        {value}
      </div>
      {unit && <div className="font-mono text-[10px] mt-0.5" style={{ color, WebkitTextFillColor: color, fontWeight: 600, opacity: 1 }}>{unit}</div>}
    </div>
  );
}

function ratingColor(ul94) {
  if (!ul94) return "#8B8B95";
  if (ul94 === "V-0") return "#39FF14";
  if (ul94 === "V-1") return "#7CFF14";
  if (ul94 === "V-2") return "#FFCC00";
  if (ul94 === "HB") return "#FF8C00";
  return "#FF3B30";
}

function euroClassColor(ec) {
  if (!ec) return "#8B8B95";
  if (ec.startsWith("A1")) return "#39FF14";
  if (ec.startsWith("A2")) return "#7CFF14";
  if (ec.startsWith("B")) return "#FFCC00";
  if (ec.startsWith("C")) return "#FF8C00";
  return "#FF3B30";
}

export default function AcademicView() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    api.get("/academic-data").then(({ data }) => {
      setItems(data);
      if (data.length) setSelectedId(data[0].material_id);
    }).catch(() => {});
  }, []);

  const sel = useMemo(() => items.find((x) => x.material_id === selectedId), [items, selectedId]);

  const stressData = useMemo(() => {
    if (!sel) return [];
    return sel.mechanical.stress_strain.map((p) => ({
      strain: Number((p.strain * 100).toFixed(2)),  // %
      stress: Number(p.stress.toFixed(1)),
    }));
  }, [sel]);

  return (
    <div className="space-y-4" data-testid="academic-view">
      {/* Hero */}
      <div className="border border-[var(--neon-cyan)] bg-gradient-to-r from-[#0A0A0A] via-[#0F1F22] to-[#0A0A0A] p-5"
        data-testid="academic-hero">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 border border-[var(--neon-cyan)] flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-7 h-7 text-[var(--neon-cyan)]"/>
          </div>
          <div className="flex-1">
            <div className="tech-label text-[var(--neon-cyan)]">{t("rdLab.hero.kicker", "R&D · ACADEMIC RESEARCH DATABASE")}</div>
            <h2
              className="text-2xl font-bold tracking-tight"
              style={{ color: "#FFFFFF", textShadow: "0 0 10px rgba(255,255,255,0.25)" }}>
              {t("rdLab.hero.title", "Academic Analysis & Test Results")}
            </h2>
            <p
              className="font-mono text-[11px] leading-relaxed mt-1"
              style={{ color: "#E5E7EB" }}>
              {t("rdLab.hero.desc", "Experimental data obtained within the scope of academic research. Compliant with ISO 5660-1 (Cone Calorimeter), ASTM D3801 (UL94), ISO 4589-2 (LOI), TS EN 12089 (Flexural) and TS EN 1607 (Tensile) standards.")}
            </p>
          </div>
        </div>
      </div>

      {/* Material selector — horizontal chip grid at top (was left aside) */}
      <div className="border-2" data-testid="academic-list" data-section="database" style={{ borderColor: '#1E40AF' }}>
        <div className="px-4 py-2 border-b flex items-center gap-2" style={{ backgroundColor: '#1E40AF', borderBottomColor: '#1E40AF' }}>
          <Beaker className="w-4 h-4" style={{ color: '#FFFFFF' }}/>
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]"
            style={{ color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', fontWeight: 800, opacity: 1, textShadow: 'none', textTransform: 'uppercase' }}>
            {t("rdLab.db.label", "DATABASE · {n} MATERIALS · Click to load").replace("{n}", items.length)}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-2 p-3" style={{ backgroundColor: '#FFFFFF' }}>
          {items.map((it) => {
            const active = it.material_id === selectedId;
            return (
              <button key={it.material_id}
                onClick={() => setSelectedId(it.material_id)}
                className="text-left transition-all"
                data-section="database"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: active ? '2px solid #A855F7' : '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  boxShadow: active ? '0 0 14px rgba(168, 85, 247, 0.25)' : 'none',
                  opacity: 1,
                  transform: active ? 'scale(1.02)' : 'scale(1)',
                }}
                onMouseEnter={(e) => {
                  if (!active) { e.currentTarget.style.borderColor = '#A855F7'; e.currentTarget.style.transform = 'scale(1.02)'; }
                }}
                onMouseLeave={(e) => {
                  if (!active) { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.transform = 'scale(1)'; }
                }}
                data-testid={`academic-item-${it.material_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[13px] truncate" style={{ color: '#A855F7', WebkitTextFillColor: '#A855F7', fontWeight: 800, opacity: 1 }}>{it.material_name}</div>
                  <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 ml-2"
                    style={{ color: '#FFFFFF', backgroundColor: '#A855F7', WebkitTextFillColor: '#FFFFFF', fontWeight: 700, borderRadius: '4px' }}>
                    {it.fire.ul94_rating}
                  </span>
                </div>
                <div className="font-mono text-[10px] mt-1 truncate" style={{ color: '#A855F7', WebkitTextFillColor: '#A855F7', fontWeight: 700, opacity: 1 }}>
                  {it.brand} · {it.category} · λ={it.lambda_value}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main detail content — full width, centered */}
      <div className="max-w-[1400px] mx-auto space-y-4">
          {!sel && (
            <div className="border border-[#1F1F1F] bg-[#141414] p-8 text-center font-mono text-sm" style={{ color: "#E5E7EB" }}>
              {t("rdLab.db.empty", "Pick a material above to load detail data.")}
            </div>
          )}
          {sel && (
            <>
              {/* Header card */}
              <div className="border border-[#1F1F1F] bg-[#141414] p-4" data-testid="academic-header">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="tech-label">{sel.brand} · {sel.category}</div>
                    <h3 className="text-xl font-bold tracking-tight">{sel.material_name}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`https://scholar.google.com/scholar?q=${encodeURIComponent(sel.material_name + " thermal insulation fire retardant")}`}
                      target="_blank" rel="noreferrer"
                      data-testid="scholar-link"
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors"
                      style={{ backgroundColor: '#EC4899', color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', border: '2px solid #EC4899', borderRadius: '6px', padding: '6px 14px', fontWeight: 700, opacity: 1 }}
                      title="Search this material on Google Scholar">
                      <BookOpen className="w-3 h-3" style={{ color: '#FFFFFF' }}/>
                      Google Scholar
                      <ExternalLink className="w-2.5 h-2.5" style={{ color: '#FFFFFF' }}/>
                    </a>
                    <span className="font-mono text-[10px] uppercase tracking-wider"
                      style={{ backgroundColor: '#EC4899', color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', border: '2px solid #EC4899', borderRadius: '6px', padding: '6px 14px', fontWeight: 700, opacity: 1 }}>
                      UL94 · {sel.fire.ul94_rating}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-wider"
                      style={{ backgroundColor: '#EC4899', color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', border: '2px solid #EC4899', borderRadius: '6px', padding: '6px 14px', fontWeight: 700, opacity: 1 }}>
                      {sel.fire.euroclass}
                    </span>
                  </div>
                </div>
              </div>

              {/* Mechanical */}
              <section className="border" data-testid="mechanical-section" style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB', borderWidth: '1px', borderRadius: '10px' }}>
                <header className="px-4 py-2 border-b flex items-center gap-2" style={{ backgroundColor: '#00CED1', borderBottomColor: '#00CED1', borderRadius: '8px 8px 0 0' }}>
                  <Activity className="w-4 h-4" style={{ color: '#FFFFFF' }}/>
                  <span
                    className="font-mono text-xs uppercase tracking-[0.2em]"
                    style={{ color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', fontWeight: 700, opacity: 1 }}>
                    {t("rdLab.mech.title", "Mechanical Properties · Stress-Strain Curve")}
                  </span>
                </header>
                <div className="p-4" style={{ backgroundColor: '#FFFFFF' }}>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-px mb-4" style={{ backgroundColor: '#00CED1', border: '1px solid #00CED1' }}>
                    <StatBox label={t("rdLab.mech.flexural", "FLEXURAL")}     value={sel.mechanical.flexural_strength_kPa}    unit="kPa (TS EN 12089)" accent="#00CED1"/>
                    <StatBox label={t("rdLab.mech.tensile", "TENSILE")}       value={sel.mechanical.tensile_strength_kPa}     unit="kPa (TS EN 1607)"  accent="#00CED1"/>
                    <StatBox label={t("rdLab.mech.compressive", "COMPRESSIVE")} value={sel.mechanical.compressive_strength_kPa} unit="kPa" accent="#00CED1"/>
                    <StatBox label={t("rdLab.mech.emodulus", "E-MODULUS")}    value={sel.mechanical.elastic_modulus_MPa}      unit="MPa"   accent="#00CED1"/>
                    <StatBox label={t("rdLab.mech.density", "DENSITY")}       value={sel.mechanical.density_kgm3}             unit="kg/m³" accent="#00CED1"/>
                  </div>
                  <div className="rd-chart-container border p-2" data-chart="true" style={{ height: 240, backgroundColor: '#000000', borderColor: '#00CED1' }} data-testid="stress-strain-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stressData} margin={{ top: 12, right: 16, bottom: 16, left: 0 }}>
                        <CartesianGrid stroke="#1E3A5F" strokeDasharray="0"/>
                        <XAxis dataKey="strain" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#00CED1" }}
                          label={{ value: "ε · Strain (%)", position: "insideBottom", offset: -2, fill: "#00CED1", fontSize: 10, fontFamily: "IBM Plex Mono" }}/>
                        <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#00CED1" }}
                          label={{ value: "σ · Stress (kPa)", angle: -90, position: "insideLeft", fill: "#00CED1", fontSize: 10, fontFamily: "IBM Plex Mono" }}/>
                        <Tooltip contentStyle={{ background: "#000000", border: "1px solid #00CED1", fontFamily: "IBM Plex Mono", fontSize: 11, color: "#00CED1" }}/>
                        <Line type="linear" dataKey="stress" stroke="#00CED1" strokeWidth={2.2}
                          dot={{ r: 3, fill: "#00CED1" }} activeDot={{ r: 6, fill: "#FFFFFF", stroke: "#00CED1", strokeWidth: 1.5 }} isAnimationActive={false}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="font-mono text-[10px] mt-2"
                    style={{ color: '#00CED1', WebkitTextFillColor: '#00CED1', fontWeight: 600, opacity: 1 }}>
                    {t("rdLab.mech.note", "Curve represents the average of 5 specimens. The post-yield region reveals the material's ductile vs. brittle character.")}
                  </p>
                </div>
              </section>

              {/* Fire */}
              <section className="border" data-testid="fire-section" data-section="fire-performance" style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB', borderRadius: '10px' }}>
                <header className="px-4 py-2 border-b flex items-center gap-2" style={{ backgroundColor: '#39FF14', borderBottomColor: '#39FF14', borderRadius: '8px 8px 0 0' }}>
                  <Flame className="w-4 h-4" style={{ color: '#000000' }}/>
                  <span
                    className="font-mono text-xs uppercase tracking-[0.2em]"
                    style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>
                    {t("rdLab.fire.title", "Fire Performance · Cone Calorimeter (ISO 5660-1)")}
                  </span>
                </header>
                <div className="p-4" style={{ backgroundColor: '#FFFFFF' }}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-px border" style={{ backgroundColor: '#E5E7EB', borderColor: '#E5E7EB' }}>
                    <StatBox light label="UL94"        value={sel.fire.ul94_rating} unit="ASTM D3801"/>
                    <StatBox light label="LOI"         value={sel.fire.loi_percent + " %"} unit="ISO 4589-2"/>
                    <StatBox light label="EUROCLASS"   value={sel.fire.euroclass} unit="EN 13501-1"/>
                    <StatBox light label={t("rdLab.fire.service", "SERVICE T.")} value={sel.fire.service_temp_range_C} unit="°C"/>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-px border mt-px" style={{ backgroundColor: '#E5E7EB', borderColor: '#E5E7EB' }}>
                    <StatBox light label="pHRR"          value={sel.fire.cone_calorimeter.peak_hrr_kWm2} unit="kW/m² (peak)"/>
                    <StatBox light label={t("rdLab.fire.ignition", "IGNITION")} value={sel.fire.cone_calorimeter.time_to_ignition_s ?? "—"} unit="s (Tig)"/>
                    <StatBox light label="THR"           value={sel.fire.cone_calorimeter.total_heat_release_MJm2} unit="MJ/m²"/>
                    <StatBox light label="SPR"           value={sel.fire.cone_calorimeter.smoke_production_m2kg} unit="m²/kg (Smoke)"/>
                  </div>
                  <p className="font-mono text-[10px] mt-3"
                    style={{ color: "#000000", WebkitTextFillColor: "#000000", fontWeight: 600, opacity: 1 }}>
                    {t("rdLab.fire.note", "The Cone Calorimeter test is performed at 50 kW/m² irradiance. A lower pHRR (peak Heat Release Rate) reduces flame spread velocity.")}
                  </p>
                </div>
              </section>

              {/* Professor's Note */}
              <section className="border-2 border-[var(--neon-cyan)] bg-[#0A1A1F]" data-testid="professor-note">
                <header className="bg-[var(--neon-cyan)] text-[#0A0A0A] px-4 py-2 flex items-center gap-2">
                  <Quote className="w-4 h-4"/>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] font-bold">
                    {t("rdLab.note.title", "Teacher's Note · Professor's Insights · Academic Research Commentary")}
                  </span>
                </header>
                <div className="p-5">
                  <p className="teachers-note-body text-sm leading-relaxed whitespace-pre-line">
                    {t("teachersNoteContent", "Teacher's Note: WR-Tech impregnation significantly improves fire performance and moisture protection. The synergy between bio-binders and flame retardants is verified through academic models.")}
                  </p>
                  <div className="mt-4 pt-3 border-t border-[var(--neon-cyan)]/40 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[var(--neon-cyan)] flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-[#0A0A0A]"/>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider neon-cyan">{t("rdLab.note.kicker", "ACADEMIC RESEARCH")}</div>
                        <div className="text-xs font-semibold">{t("rdLab.note.lab", "Building Physics & Materials Science Laboratory")}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[10px] text-[#8B8B95]">{t("rdLab.note.peer", "PEER-REVIEWED · ACADEMIC")}</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* R&D Simulation Panel */}
              <RDSimulationPanel selected={sel}/>

              {/* EPS-specific Advanced Additive Simulation */}
              <EPSSimulationPanel/>

              {/* Rockwool Module */}
              <RockwoolModule/>

              {/* PUR/PIR Module */}
              <PURModule/>

              {/* Glasswool Module */}
              <GlasswoolModule/>
            </>
          )}
      </div>
    </div>
  );
}
